import React, { useEffect, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControlLabel,
    Switch,
    TextField,
    Typography
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CustomModal from 'src/components/modal/customModal';
import { axiosGetCall, axiosPostCall } from 'src/services/apisCall';
import { renderErrorMessage, renderSuccessMessage } from 'src/lib/toastMessage';
import Logger from 'src/lib/Logger';

const logger = new Logger("assignViewModal");

const STATUS_COLORS = {
    active: { bg: '#e6f4ea', color: '#1e7e34' },
    deActivate: { bg: '#fdecea', color: '#c62828' },
};

const getStatusChip = (status) => {
    const style = STATUS_COLORS[status] || {};
    return (
        <Chip
            label={status}
            size="small"
            sx={{
                ml: 1,
                height: 18,
                fontSize: 10,
                backgroundColor: style.bg,
                color: style.color,
                fontWeight: 600,
                textTransform: 'capitalize',
            }}
        />
    );
};

// Derive Autocomplete value (admin objects) from viewableAdminsAccess emails.
// Falls back to a stub object so pre-filled admins always show even if deactivated.
const toAdminObjects = (viewableAdminsAccess, admins) => {
    const adminMap = Object.fromEntries(admins.map((a) => [a.email, a]));
    return viewableAdminsAccess.map(({ email }) => adminMap[email] || { email, role: 'admin', status: 'deActivate' });
};

// Merge new admin selection into viewableAdminsAccess, preserving existing agentVisibility
const mergeSelection = (selected, prevAccess) => {
    const prevMap = Object.fromEntries(prevAccess.map((entry) => [entry.email, entry]));
    return selected.map(({ email }) => prevMap[email] || { email, agentVisibility: false });
};

const AssignViewModal = ({ open, close }) => {
    const [admins, setAdmins] = useState([]);
    const [targetAdmin, setTargetAdmin] = useState(null);
    const [viewableAdminsAccess, setViewableAdminsAccess] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingAssigned, setLoadingAssigned] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const fetchAdmins = async () => {
        try {
            setLoading(true);
            const { result = [] } = await axiosGetCall('getAllAdmin');
            setAdmins(result);
        } catch (error) {
            logger.error("assignViewModal fetchAdmins", error);
            renderErrorMessage(error?.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchAssignedView = async (email) => {
        try {
            setLoadingAssigned(true);
            const { viewableAdminsAccess: assigned = [] } = await axiosGetCall('getAssignedView', { email });
            setViewableAdminsAccess(assigned);
        } catch (error) {
            logger.error("assignViewModal fetchAssignedView", error);
            renderErrorMessage(error?.message);
        } finally {
            setLoadingAssigned(false);
        }
    };

    const handleTargetChange = (value) => {
        setTargetAdmin(value);
        setViewableAdminsAccess([]);
        if (value) fetchAssignedView(value.email);
    };

    const handleClose = () => {
        setTargetAdmin(null);
        setViewableAdminsAccess([]);
        close();
    };

    const handleSelectionChange = (_, selected) => {
        setViewableAdminsAccess((prev) => mergeSelection(selected, prev));
    };

    const toggleAgentVisibility = (email) => {
        setViewableAdminsAccess((prev) =>
            prev.map((entry) =>
                entry.email === email ? { ...entry, agentVisibility: !entry.agentVisibility } : entry
            )
        );
    };

    const handleSubmit = async () => {
        if (!targetAdmin) {
            renderErrorMessage("Please select an admin to configure.");
            return;
        }

        if (viewableAdminsAccess.some(({ email }) => email === targetAdmin.email)) {
            renderErrorMessage("An admin cannot view themselves.");
            return;
        }

        try {
            setSubmitting(true);
            await axiosPostCall('updateAssignedView', {
                email: targetAdmin.email,
                viewableAdminsAccess,
            });
            renderSuccessMessage("Assign View updated successfully.");
            handleClose();
        } catch (error) {
            logger.error("assignViewModal handleSubmit", error);
            renderErrorMessage(error?.message);
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => {
        if (open) fetchAdmins();
    }, [open]);

    const renderOption = (props, option) => (
        <Box component="li" {...props} key={option.email} sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ flexGrow: 1 }}>{option.email}</Typography>
            {getStatusChip(option.status)}
        </Box>
    );

    const selectedAdminObjects = toAdminObjects(viewableAdminsAccess, admins);

    const description = (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" sx={{ color: 'grey.600' }}>
                Grant an admin visibility into another admin's customers (and optionally agents).
            </Typography>

            {/* Target admin picker */}
            <Autocomplete
                size="small"
                options={admins.filter(({ role, status }) => (role === 'admin' || role === 'superAdmin') && status === 'active')}
                value={targetAdmin}
                loading={loading}
                getOptionLabel={(option) => option.email || ''}
                isOptionEqualToValue={(option, value) => option.email === value.email}
                onChange={(_, value) => handleTargetChange(value)}
                renderOption={renderOption}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Admin to configure"
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {loading ? <CircularProgress color="inherit" size={16} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
            />

            {/* Viewable admins multi-select */}
            <Autocomplete
                multiple
                size="small"
                options={admins.filter(({ email, role }) =>
                    email !== targetAdmin?.email &&
                    (role === 'admin' || role === 'superAdmin')
                )}
                value={selectedAdminObjects}
                loading={loadingAssigned}
                disabled={!targetAdmin || loadingAssigned}
                getOptionLabel={(option) => option.email || ''}
                isOptionEqualToValue={(option, value) => option.email === value.email}
                onChange={handleSelectionChange}
                renderOption={renderOption}
                renderTags={(selected, getTagProps) =>
                    selected.map((option, index) => (
                        <Chip
                            key={option.email}
                            label={option.email}
                            size="small"
                            {...getTagProps({ index })}
                        />
                    ))
                }
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Can also view"
                        placeholder={targetAdmin ? "Select admins…" : "Select an admin first"}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {loadingAssigned ? <CircularProgress color="inherit" size={16} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
            />

            {/* Per-admin agentVisibility toggles */}
            {viewableAdminsAccess.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="caption" sx={{ color: 'grey.500', mb: 0.5 }}>
                        Visibility settings per admin
                    </Typography>
                    {viewableAdminsAccess.map(({ email, agentVisibility }) => (
                        <Box
                            key={email}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 1,
                                backgroundColor: 'grey.100',
                            }}
                        >
                            <Typography variant="body2" sx={{ flexGrow: 1, fontSize: 12 }}>
                                {email}
                            </Typography>
                            <FormControlLabel
                                control={
                                    <Switch
                                        size="small"
                                        checked={agentVisibility}
                                        onChange={() => toggleAgentVisibility(email)}
                                    />
                                }
                                label={
                                    <Typography variant="caption" sx={{ color: 'grey.600' }}>
                                        Include agents
                                    </Typography>
                                }
                                labelPlacement="start"
                                sx={{ m: 0, gap: 0.5 }}
                            />
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );

    const action = (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button variant="outlined" onClick={handleClose} disabled={submitting}>
                Cancel
            </Button>
            <Button
                variant="contained"
                startIcon={submitting ? <CircularProgress color="inherit" size={16} /> : <VisibilityIcon />}
                disabled={submitting || loading || !targetAdmin}
                onClick={handleSubmit}
                sx={{
                    backgroundColor: 'rgb(33, 43, 54)',
                    '&:hover': { backgroundColor: 'rgb(69, 79, 91)' }
                }}
            >
                Save
            </Button>
        </Box>
    );

    return (
        <CustomModal
            open={open}
            close={handleClose}
            title="Assign View"
            description={description}
            action={action}
            width={480}
        />
    );
};

export default AssignViewModal;
